/**
 * Buyer's Leverage brand mark — house with inspection clipboard
 * (priority dots in moss/amber/rust) + protective shield with checkmark.
 *
 * Design tokens:
 *   Deep Teal    #0F3D3A
 *   Moss Green   #6B8F6A
 *   Amber        #D4A62A
 *   Rust         #C85B3A
 *   Warm Off-W   #FAF7F2
 */

const C = {
    teal: "#0F3D3A",
    moss: "#6B8F6A",
    amber: "#D4A62A",
    rust: "#C85B3A",
    bg: "#FAF7F2",
};

/**
 * Square brand mark (icon-only). Defaults to 32x32.
 * Pass `size` in px or `className` (sets w/h via tailwind).
 */
export function BrandMark({ size = 32, className = "", title = "Buyer's Leverage" }) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 64 64"
            width={size}
            height={size}
            className={className}
            role="img"
            aria-label={title}
            data-testid="brand-mark"
        >
            <title>{title}</title>
            {/* House silhouette (stroke) */}
            <path
                d="M8 30 L32 10 L52 26 V26 L52 14 H46 V21 L32 10 L8 30 Z M12 30 V54 H52 V30"
                fill="none"
                stroke={C.teal}
                strokeWidth="3.5"
                strokeLinejoin="round"
                strokeLinecap="round"
            />
            {/* Inspection clipboard / report */}
            <rect
                x="17"
                y="22"
                width="22"
                height="26"
                rx="2.2"
                fill={C.bg}
                stroke={C.teal}
                strokeWidth="2.6"
            />
            {/* Clip at top of clipboard */}
            <rect
                x="24"
                y="20"
                width="8"
                height="3.6"
                rx="1.1"
                fill={C.teal}
            />
            {/* Priority rows: dot + line */}
            <circle cx="22" cy="30" r="1.8" fill={C.moss} />
            <rect x="26" y="29" width="11" height="2" rx="1" fill={C.teal} opacity="0.85" />
            <circle cx="22" cy="36" r="1.8" fill={C.amber} />
            <rect x="26" y="35" width="11" height="2" rx="1" fill={C.teal} opacity="0.85" />
            <circle cx="22" cy="42" r="1.8" fill={C.rust} />
            <rect x="26" y="41" width="11" height="2" rx="1" fill={C.teal} opacity="0.85" />
            {/* Shield with checkmark (bottom-right) */}
            <path
                d="M38 38 L52 38 L52 50 C52 55 46 58 45 58 C44 58 38 55 38 50 Z"
                fill={C.teal}
                stroke={C.teal}
                strokeWidth="1"
                strokeLinejoin="round"
            />
            <path
                d="M41.5 48 L44 50.5 L48.5 45.5"
                fill="none"
                stroke={C.bg}
                strokeWidth="2.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/**
 * Horizontal wordmark lockup: BrandMark + "Buyer's Leverage" + tag.
 * Used in the navbar / footer / auth header.
 */
export function BrandLockup({ size = 32, className = "", tag = "Inspection Intelligence" }) {
    return (
        <div className={"flex items-center gap-2.5 " + className} data-testid="brand-lockup">
            <BrandMark size={size} />
            <div className="leading-tight">
                <div className="font-display font-semibold text-bl text-[15px] tracking-tight">
                    Buyer's Leverage
                </div>
                <div className="text-[10px] tracking-[0.22em] text-bl-muted uppercase">
                    {tag}
                </div>
            </div>
        </div>
    );
}

export default BrandMark;
